import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSession } from '../services/authService';
import { submitBetaTesterFeedback } from '../services/platformBetaTesterFeedbackService';
import {
  EMPTY_BETA_TESTER_FEEDBACK_DRAFT,
  loadBetaTesterFeedbackDraft,
  saveBetaTesterFeedbackDraft,
  type BetaTesterFeedbackDraft,
} from '../utils/betaTesterFeedbackDraftStorage';
import {
  getBoundSubscriptionPassUserId,
  hasActiveBetaTesterPass,
  loadSubscriptionPassState,
  SUBSCRIPTION_PASS_CHANGED_EVENT,
} from '../utils/subscriptionPassStorage';

type FeedbackSectionKey = keyof BetaTesterFeedbackDraft;

type FeedbackDraft = BetaTesterFeedbackDraft;

const EMPTY_DRAFT: FeedbackDraft = { ...EMPTY_BETA_TESTER_FEEDBACK_DRAFT };

const FEEDBACK_MAIL = 'edukodi.info@gmail.com';

const SECTIONS: {
  key: FeedbackSectionKey;
  label: string;
  placeholder: string;
}[] = [
  {
    key: 'goodPoints',
    label: '좋은점',
    placeholder: '사용해 보며 좋았던 점, 마음에 든 기능을 적어 주세요.',
  },
  {
    key: 'useCases',
    label: '활용방안',
    placeholder: '수업·업무에서 어떻게 활용하면 좋을지 적어 주세요.',
  },
  {
    key: 'improvements',
    label: '개선하면 좋은 점',
    placeholder: '아쉬운 점이나 개선되면 좋겠다고 느낀 점을 적어 주세요.',
  },
  {
    key: 'bugs',
    label: '버그',
    placeholder: '발견한 오류·이상 동작과 재현 방법을 적어 주세요.',
  },
];

const BetaTesterFeedbackPage: React.FC = () => {
  const [isBeta, setIsBeta] = useState<boolean | null>(null);
  const [draft, setDraft] = useState<FeedbackDraft>(EMPTY_DRAFT);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [submittingKey, setSubmittingKey] = useState<FeedbackSectionKey | null>(null);
  const [confirmKey, setConfirmKey] = useState<FeedbackSectionKey | null>(null);
  const [mailCopied, setMailCopied] = useState(false);
  const [msgByKey, setMsgByKey] = useState<
    Partial<Record<FeedbackSectionKey, { ok: boolean; text: string }>>
  >({});
  const [sessionMeta, setSessionMeta] = useState<{
    userId: string | null;
    email: string;
    fullName: string;
  }>({ userId: null, email: '', fullName: '' });

  const draftUserId = sessionMeta.userId ?? getBoundSubscriptionPassUserId();

  const refreshBeta = useCallback(() => {
    const uid = getBoundSubscriptionPassUserId();
    setIsBeta(hasActiveBetaTesterPass(loadSubscriptionPassState(uid)));
  }, []);

  useEffect(() => {
    refreshBeta();
    window.addEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, refreshBeta);
    return () => window.removeEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, refreshBeta);
  }, [refreshBeta]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await getSession();
        if (cancelled) return;
        const user = session?.user;
        const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
        const fullName =
          (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
          (typeof meta.name === 'string' && meta.name.trim()) ||
          '';
        setSessionMeta({
          userId: user?.id ?? null,
          email: (user?.email ?? '').trim(),
          fullName,
        });
      } catch {
        if (!cancelled) {
          setSessionMeta({ userId: null, email: '', fullName: '' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setDraft(loadBetaTesterFeedbackDraft(draftUserId));
    setDraftHydrated(true);
  }, [draftUserId]);

  useEffect(() => {
    if (!draftHydrated) return;
    saveBetaTesterFeedbackDraft(draft, draftUserId);
  }, [draft, draftHydrated, draftUserId]);

  const confirmSection = useMemo(
    () => (confirmKey ? SECTIONS.find((s) => s.key === confirmKey) ?? null : null),
    [confirmKey],
  );
  const confirmText = confirmKey ? draft[confirmKey].trim() : '';

  const copyFeedbackMail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(FEEDBACK_MAIL);
      setMailCopied(true);
      window.setTimeout(() => setMailCopied(false), 2000);
    } catch {
      setMailCopied(false);
      window.alert('메일 주소를 복사하지 못했습니다. 직접 선택해 복사해 주세요.');
    }
  }, []);

  const openConfirm = (key: FeedbackSectionKey) => {
    if (submittingKey) return;
    const text = draft[key].trim();
    if (!text) {
      setMsgByKey((m) => ({
        ...m,
        [key]: { ok: false, text: '내용을 입력한 뒤 보내 주세요.' },
      }));
      return;
    }
    setMsgByKey((m) => {
      const next = { ...m };
      delete next[key];
      return next;
    });
    setConfirmKey(key);
  };

  const closeConfirm = () => {
    if (submittingKey) return;
    setConfirmKey(null);
  };

  const handleConfirmSend = async () => {
    if (!confirmKey || submittingKey) return;
    const key = confirmKey;
    const text = draft[key].trim();
    if (!text) {
      setConfirmKey(null);
      setMsgByKey((m) => ({
        ...m,
        [key]: { ok: false, text: '내용을 입력한 뒤 보내 주세요.' },
      }));
      return;
    }
    try {
      setSubmittingKey(key);
      const payload = {
        goodPoints: '',
        useCases: '',
        improvements: '',
        bugs: '',
        [key]: text,
        fullName: sessionMeta.fullName,
        email: sessionMeta.email,
        userId: sessionMeta.userId,
      };
      const result = await submitBetaTesterFeedback(payload);
      if (!result.ok) {
        if (result.missingTable) {
          setMsgByKey((m) => ({
            ...m,
            [key]: {
              ok: false,
              text: '의견 저장 테이블이 아직 없습니다. 관리자에게 마이그레이션 적용을 요청해 주세요.',
            },
          }));
        } else {
          setMsgByKey((m) => ({
            ...m,
            [key]: {
              ok: false,
              text: result.error || '의견을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.',
            },
          }));
        }
        return;
      }
      setDraft((prev) => ({ ...prev, [key]: '' }));
      setConfirmKey(null);
      setMsgByKey((m) => ({
        ...m,
        [key]: { ok: true, text: '보냈습니다. 소중한 의견 감사합니다.' },
      }));
    } catch (err) {
      console.error(err);
      setMsgByKey((m) => ({
        ...m,
        [key]: { ok: false, text: '의견을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.' },
      }));
    } finally {
      setSubmittingKey(null);
    }
  };

  if (isBeta === null) {
    return (
      <section className="cp-beta-feedback-page" aria-busy="true">
        <p className="cp-dashboard-placeholder" role="status">
          로딩 중…
        </p>
      </section>
    );
  }

  if (!isBeta) {
    return (
      <section className="cp-beta-feedback-page" aria-labelledby="cp-beta-feedback-title">
        <div className="cp-page-title-row cp-beta-feedback-title-row">
          <h2 id="cp-beta-feedback-title">베타 테스터 노트</h2>
        </div>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 베타 테스터 이용권을 등록한 계정만 이용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section className="cp-beta-feedback-page" aria-labelledby="cp-beta-feedback-title">
      <div className="cp-page-title-row cp-beta-feedback-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-beta-feedback-title">베타 테스터 노트</h2>
          <div className="cp-beta-feedback-guide">
            <p className="cp-admin-legal-subtitle">
              섹션별 의견을 남겨 보내 주세요. 이미지, 첨부파일 등 구체적인 자료와 함께 의견을
              보내실 땐 아래 메일 주소로 의견을 보내 주세요.
            </p>
            <div className="cp-beta-feedback-mail-row">
              <a className="cp-beta-feedback-mail" href={`mailto:${FEEDBACK_MAIL}`}>
                {FEEDBACK_MAIL}
              </a>
              <button
                type="button"
                className="cp-btn-secondary cp-beta-feedback-mail-copy"
                onClick={() => void copyFeedbackMail()}
              >
                {mailCopied ? '복사됨' : '복사하기'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="cp-beta-feedback-form">
        {SECTIONS.map((section) => {
          const msg = msgByKey[section.key];
          const headId = `cp-beta-feedback-head-${section.key}`;
          return (
            <article
              key={section.key}
              className="cp-dashboard-block cp-admin-users-pass-block cp-beta-feedback-section-block"
              aria-labelledby={headId}
            >
              <h3 className="cp-dashboard-block__head" id={headId}>
                <span className="cp-dashboard-block__head-title">{section.label}</span>
              </h3>
              <div className="cp-dashboard-block__body cp-admin-users-pass-block-body cp-beta-feedback-section-body">
                <textarea
                  id={`cp-beta-feedback-${section.key}`}
                  className="cp-beta-feedback-textarea"
                  rows={8}
                  maxLength={5000}
                  placeholder={section.placeholder}
                  aria-label={section.label}
                  value={draft[section.key]}
                  disabled={submittingKey != null}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDraft((prev) => ({
                      ...prev,
                      [section.key]: value,
                    }));
                    setMsgByKey((m) => {
                      if (!m[section.key]) return m;
                      const next = { ...m };
                      delete next[section.key];
                      return next;
                    });
                  }}
                />
                {msg && !msg.ok ? (
                  <p className="cp-beta-feedback-msg is-error" role="alert">
                    {msg.text}
                  </p>
                ) : null}
                <div className="cp-beta-feedback-actions cp-admin-users-pass-issue-actions--block">
                  <button
                    type="button"
                    className={`cp-btn-primary cp-admin-users-pass-issue-create-btn${msg?.ok ? ' is-sent' : ''}`}
                    disabled={submittingKey != null || !draft[section.key].trim() || Boolean(msg?.ok)}
                    onClick={() => openConfirm(section.key)}
                  >
                    {msg?.ok
                      ? msg.text
                      : submittingKey === section.key
                        ? '보내는 중…'
                        : '보내기'}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {confirmSection && confirmKey ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={closeConfirm}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeConfirm();
          }}
        >
          <div
            className="cp-modal cp-beta-feedback-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-beta-feedback-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-beta-feedback-confirm-title">{confirmSection.label} 의견 보내기</h3>
            <div className="cp-modal-body">
              <div className="cp-beta-feedback-confirm-preview" tabIndex={0}>
                {confirmText}
              </div>
              <p className="cp-beta-feedback-confirm-ask">위 내용으로 의견을 보내겠습니까?</p>
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={closeConfirm}
                disabled={submittingKey != null}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleConfirmSend()}
                disabled={submittingKey != null}
              >
                {submittingKey === confirmKey ? '보내는 중…' : '보내기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default BetaTesterFeedbackPage;
